import { useEffect, useState } from "react";
import SmartWizLogo from "../Logos/SmartWizLogo";
import { Loader } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import ErrorPage from "../../pages/ErrorPage/ErrorPage";
import {
  getNotifications,
  getPersonalInfo,
  getTaxProInfo,
} from "@utils/api.ts";
import CustomLogo from "../Logos/CustomLogo";
import "./SmartWizLoader.scss";
import { AppRoutes, BUSINESS_STAFF } from "@/constants.ts";
import { fetchBrandingInfo } from "@pages/LoginManagement/hooks/useBranding.ts";
import { EXT_IDS } from "@constants/Extension.ts";
import { useAuthStore } from "@utils/stores/authStore.ts";
import {
  getIntakeLoginDestination,
  shouldRedirectToIntakeAtLogin,
} from "@/utils/frontendMigration";

// Extend Window interface for extension ID
declare global {
  interface Window {
    smartwizExtensionId?: string;
  }
}

/**
 * Notify Chrome extension about successful login
 */
const notifyExtensionOfLogin = () => {
  try {
    // Check if we're in a browser environment with Chrome extension API
    if (
      typeof window !== "undefined" &&
      (window as any).chrome &&
      (window as any).chrome.runtime &&
      (window as any).chrome.runtime.sendMessage
    ) {
      console.log(
        "[CRM] Attempting to notify SmartWiz extension of login success"
      );

      // For published extensions, we could use a hardcoded extension ID
      // For development/unpacked extensions, the ID changes each time
      // We'll use a window postMessage approach that the content script can listen for

      const message = {
        type: "CRM_LOGIN_SUCCESS",
        timestamp: new Date().toISOString(),
        source: "smartwiz_crm",
      };

      // First try: Use window postMessage for content script to pick up
      window.postMessage(message, window.location.origin);
      console.log("[CRM] Posted message to window for content script");

      // Second try: Attempt direct extension messaging
      // Use externally_connectable to send message directly to extension
      const tryExtensionMessaging = async () => {
        try {
          // Try to send message to extension directly using externally_connectable
          // The extension manifest allows this domain to send external messages

          // First, check if extension ID is available (injected by content script)
          let extensionId = window.smartwizExtensionId;

          // If no extension ID available, try common published extension IDs
          if (!extensionId) {
            // Try each known ID
            for (const id of EXT_IDS) {
              try {
                (window as any).chrome.runtime.sendMessage(id, message);
                console.log(
                  `[CRM] Attempted direct messaging to extension ID: ${id}`
                );
                return;
              } catch (e) {
                // Continue to next ID
              }
            }
          } else {
            // Use injected extension ID
            (window as any).chrome.runtime.sendMessage(
              extensionId,
              message,
              (response: any) => {
                if ((window as any).chrome.runtime.lastError) {
                  console.log(
                    "[CRM] Direct extension messaging failed:",
                    (window as any).chrome.runtime.lastError.message
                  );
                } else {
                  console.log(
                    "[CRM] Successfully notified extension via direct messaging",
                    response
                  );
                }
              }
            );
          }
        } catch (error) {
          console.log("[CRM] Direct extension messaging error:", error);
        }
      };

      tryExtensionMessaging();
    } else {
      console.log("[CRM] Chrome extension API not available");
    }
  } catch (error) {
    console.log("[CRM] Error notifying extension:", error);
  }
};

/**
 * SmartWizLoader component responsible for loading data and rendering components based on fetched data.
 *
 * @return {JSX.Element} JSX element representing the SmartWizLoader component
 */
const SmartWizLoader = () => {
  const [error, setError] = useState<boolean | null>(false);
  const navigate = useNavigate();
  const { user, setRole, refreshAuth } = useAuthStore();

  useEffect(() => {
    // Set a timeout for showing error page if loading takes too long
    const errorTimeout = setTimeout(() => {
      setError(true);
    }, 25000); // 25 seconds

    const fetchData = async () => {
      try {
        await refreshAuth();
        const sessionUser = useAuthStore.getState().user;

        if (sessionUser && shouldRedirectToIntakeAtLogin(sessionUser)) {
          notifyExtensionOfLogin();
          window.location.replace(getIntakeLoginDestination(sessionUser));
          return;
        }

        const personalInfo = await getPersonalInfo();

        const userId = personalInfo?.data?.content.userId;
        const resolvedRole = personalInfo?.data?.content.role;
        const businessId = personalInfo?.data?.content.businessId;

        localStorage.setItem("businessId", businessId);
        localStorage.setItem("role", resolvedRole);
        localStorage.setItem("isInitialized", "true");

        // Update the authStore with the fetched role
        setRole(resolvedRole);

        // Notify Chrome extension about successful login after authentication is confirmed
        notifyExtensionOfLogin();

        await getTaxProInfo(businessId);

        // Fetch and store:
        // - onboarding status
        if (userId) {
          // Walkthrough status checks removed
        }

        // Use fetchBrandingInfo to get logoUrl
        const brandingInfo = await fetchBrandingInfo(businessId);
        if (brandingInfo.logoUrl) {
          sessionStorage.setItem("logoUrl", brandingInfo.logoUrl);
        } else {
          sessionStorage.removeItem("logoUrl");
        }

        // Pending onboarding owners finish bootstrap before legacy onboarding.
        const frontendMode = sessionUser?.frontendMode ?? "unset";
        if (
          frontendMode === "intake_pending_onboarding" &&
          resolvedRole === "owner"
        ) {
          navigate(AppRoutes.gettingStarted);
          return;
        }

        // Navigate based on role and useLiteHome flag
        // useLiteHome defaults to true for new businesses, false for legacy
        const useLiteHome =
          sessionUser?.useLiteHome ?? user?.useLiteHome ?? true;

        if (BUSINESS_STAFF.includes(resolvedRole)) {
          // Route to lite home or legacy home based on business setting
          navigate(useLiteHome ? AppRoutes.homeLite : AppRoutes.home);
        } else if (resolvedRole === "client") {
          navigate(AppRoutes.taxOrganizerHome);
        } else {
          console.error("Invalid login role:", resolvedRole);
          navigate(AppRoutes.login);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(true);
      }
    };

    const fetchNotifications = async () => await getNotifications({});

    fetchData();
    fetchNotifications();

    return () => clearTimeout(errorTimeout);
  }, [navigate]);

  // Render ErrorPage component if error state is true
  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="loader">
      <span>
        {sessionStorage.getItem("logoUrl") ? (
          <CustomLogo
            isCustom
            customUrl={sessionStorage.getItem("logoUrl") as string}
          />
        ) : (
          <SmartWizLogo />
        )}
        <Loader color="blue" />
      </span>
    </div>
  );
};

export default SmartWizLoader;
